import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@safetyhub/ui';
import { useAuthStore } from '../stores/auth';
import { useModalStore } from '../stores/modal';
import { MEMBER_LABEL, OWNER_LABEL, organizationsApi } from '../lib/organizations';
import { hasMembershipPermission } from '../lib/membershipAccess';
import { CheckIcon, ChevronDownIcon } from './icons';
import CreateOrganizationModal from './modals/CreateOrganizationModal';

interface OrganizationSelectorProps {
  /** 컴팩트 모드 (모바일 헤더용) */
  compact?: boolean;
}

export default function OrganizationSelector({ compact = false }: OrganizationSelectorProps) {
  const navigate = useNavigate();
  const { user, currentMembership, setCurrentOrganization, fetchUser } = useAuthStore();
  const { openCreateOrganization } = useModalStore();
  const [isOpen, setIsOpen] = useState(false);
  const [restoringOrgId, setRestoringOrgId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 활성 조직만 필터링 (삭제되지 않은 조직)
  // deletedAt이 있으면 soft delete 상태 (복구 가능)
  const activeMemberships = user?.memberships.filter(
    (m) => !m.company.deletedAt
  ) || [];

  // 삭제 예정 조직 중 **내가 복구할 수 있는** 것 (deletedAt이 있고 30일 이내) — 복구 카드 대상.
  // deletedAt: soft delete 시점 (복구 가능 기간 계산용)
  // P6-b: 옛 `role === 'OWNER'` → 권한 **key**(`org.delete`). 서버 복구 게이트가 그 키이므로
  // (`organizations.controller.ts` restore) 성질로 좁히면 키를 가진 사람에게 카드가 안 뜬다.
  // 소유자는 전체 키를 받으므로 이 판정은 소유자를 이미 포함한다(`lib/membershipAccess.ts` doc).
  const deletedMemberships = user?.memberships.filter((m) => {
    // deletedAt이 반드시 있어야 함 (soft delete 상태)
    if (!m.company.deletedAt) return false;
    if (!hasMembershipPermission(m, 'org.delete')) return false;

    const deletedAt = new Date(m.company.deletedAt);
    const now = new Date();
    const daysSinceDeleted = Math.floor((now.getTime() - deletedAt.getTime()) / (1000 * 60 * 60 * 24));
    return daysSinceDeleted <= 30;
  }) || [];

  // 현재 선택된 조직 정보 가져오기
  const currentOrg = user?.memberships.find(
    (m) => m.companyId === currentMembership?.companyId
  );

  // 표시할 조직명
  const displayName = currentOrg?.company.name || '조직 선택';

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (companyId: string) => {
    // 같은 조직 선택 시 무시
    if (companyId === currentMembership?.companyId) {
      setIsOpen(false);
      return;
    }

    setCurrentOrganization(companyId);
    setIsOpen(false);
    navigate('/'); // 대시보드로 이동
  };

  const handleRestore = async (e: React.MouseEvent, companyId: string) => {
    e.stopPropagation(); // 드롭다운 닫힘 방지

    if (restoringOrgId) return; // 이미 복구 중이면 무시

    setRestoringOrgId(companyId);
    try {
      await organizationsApi.restore(companyId);
      await fetchUser(); // 사용자 정보 갱신
    } catch (error) {
      console.error('조직 복구 실패:', error);
      alert('조직 복구에 실패했습니다.');
    } finally {
      setRestoringOrgId(null);
    }
  };

  // 남은 복구 가능 일수 계산 (deletedAt 기준 30일)
  const getRemainingDays = (org: { deletedAt?: string | null }): number => {
    if (!org.deletedAt) return 0;
    const deleted = new Date(org.deletedAt);
    const now = new Date();
    const daysSinceDeleted = Math.floor((now.getTime() - deleted.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - daysSinceDeleted);
  };

  // 소유자 성질(배정 축, P7-b-2 — 옛 `role === 'OWNER'`). 스위처의 조직은 전부 구성원 조직(/auth/me가 그렇게 싣는다)
  const getRoleBadge = (isOwner: boolean) => {
    if (isOwner) {
      return (
        <Badge tone="brand" shape="rounded">
          {OWNER_LABEL}
        </Badge>
      );
    }
    return (
      <Badge tone="slate" shape="rounded">
        {MEMBER_LABEL}
      </Badge>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 선택된 조직 표시 버튼 */}
      {compact ? (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 text-sm text-ink-700 hover:bg-surface-hover rounded-card-sm transition-colors"
        >
          <span className="truncate max-w-24">{displayName}</span>
          <ChevronDownIcon className={`h-3.5 w-3.5 text-ink-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          // 시안 `.ws` — 기본은 soft 면 + 옅은 테두리, hover는 **흰색으로 밝아지고** 테두리가 진해진다.
          // 매핑표의 `hover:bg-surface-hover`를 그대로 쓰면 soft 바탕에서 오히려 어두워져 방향이 뒤집힌다.
          className="w-full flex items-center justify-between px-3 py-[11px] text-left bg-surface-soft border border-line hover:bg-white hover:border-ink-300 rounded-card transition-colors"
        >
          <div className="flex-1 min-w-0">
            {/* 시안 `.ws-l1` 13.5px/500 — uppercase·tracking은 시안에 없다(한글이라 무의미) */}
            <p className="text-[13.5px] font-medium leading-tight text-ink-500">현재 작업공간</p>
            {/* 시안 `.ws-l2` 16px/700 */}
            <p className="text-base font-bold leading-tight tracking-[-0.01em] text-ink-900 truncate">
              {displayName}
            </p>
          </div>
          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-ink-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      )}

      {/* 드롭다운 메뉴 */}
      {isOpen && (
        <div className={`absolute mt-1 bg-white rounded-card shadow-card border border-line py-1 z-50 max-h-80 overflow-y-auto ${
          // min-w-48(192px)은 조직명이 `(주)카이...`로 잘렸다(2026-07-28 폴드 실측). 좁은 화면에서도
          // 뷰포트를 넘지 않도록 max-w로 상한을 둔다.
          compact ? 'right-0 min-w-64 max-w-[calc(100vw-2rem)]' : 'left-0 right-0'
        }`}>
          {/* 조직 목록 */}
          {activeMemberships.map((membership) => {
            const isSelected = membership.companyId === currentMembership?.companyId;
            return (
              <button
                key={membership.companyId}
                onClick={() => handleSelect(membership.companyId)}
                className={`w-full px-3 py-2 text-left hover:bg-surface-hover flex items-center justify-between ${
                  isSelected ? 'bg-brand-50' : ''
                }`}
              >
                <div className="flex items-center min-w-0">
                  {isSelected && (
                    <CheckIcon className="h-4 w-4 text-brand-600 mr-2 flex-shrink-0" />
                  )}
                  <BuildingIcon className={`w-4 h-4 mr-2 flex-shrink-0 ${
                    isSelected ? 'text-brand-600' : 'text-ink-400'
                  }`} />
                  <span
                    className={`text-sm truncate ${
                      isSelected ? 'font-medium text-brand-700' : 'text-ink-700'
                    }`}
                  >
                    {membership.company.name ?? ''}
                  </span>
                </div>
                {getRoleBadge(membership.isOwner === true)}
              </button>
            );
          })}

          {/* 삭제 예정 조직 섹션 */}
          {deletedMemberships.length > 0 && (
            <>
              <div className="border-t border-line my-1">
                <p className="px-3 py-1.5 text-xs font-medium text-red-400 uppercase tracking-wider">
                  삭제 예정
                </p>
              </div>
              {deletedMemberships.map((membership) => {
                const remainingDays = getRemainingDays(membership.company);
                const isRestoring = restoringOrgId === membership.companyId;
                return (
                  <div
                    key={membership.companyId}
                    className="w-full px-3 py-2 flex items-center justify-between bg-red-50/50"
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      <BuildingIcon className="w-4 h-4 mr-2 flex-shrink-0 text-red-400" />
                      <span className="text-sm text-ink-500 truncate">
                        {membership.company.name ?? ''}
                      </span>
                      <span className="ml-2 text-xs text-red-500 flex-shrink-0">
                        D-{remainingDays}
                      </span>
                    </div>
                    <button
                      onClick={(e) => handleRestore(e, membership.companyId)}
                      disabled={isRestoring}
                      className="ml-2 text-xs text-brand-600 hover:text-brand-800 font-medium disabled:opacity-50 flex-shrink-0"
                    >
                      {isRestoring ? '복구 중...' : '복구하기'}
                    </button>
                  </div>
                );
              })}
            </>
          )}

          {/* 구분선 + 새 조직 만들기 */}
          <div className="border-t border-line mt-1 pt-1">
            <button
              onClick={() => {
                setIsOpen(false);
                openCreateOrganization();
              }}
              className="w-full px-3 py-2 text-left hover:bg-surface-hover flex items-center text-sm text-brand-600"
            >
              <span className="text-base mr-2 leading-none">+</span>
              새 조직 만들기
            </button>
          </div>
        </div>
      )}

      {/* 조직 생성 모달 */}
      <CreateOrganizationModal />
    </div>
  );
}

// Icons
function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}
